import React, { useState, useEffect, useMemo } from "react";
import axiosInstance from "../../../services/axios.instance";
import { API_URL } from "../../../services/apiConfig";
import * as XLSX from "xlsx-js-style";
import { AflApiResponse } from "../../../types";
import { Button, Col, Form, FormControl, Row } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import LoaderExportFileKirkosModal from "../../../components/LoaderExportFileKirkosModal";
import ConfirmationModal from "../../../components/ConfirmationModal";

const ExportorderStatus = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [allSearchResults, setAllSearchResults] = useState<any[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [orderCounts, setOrderCounts] = useState({
    delivered_orders: 0,
    all_orders: 0,
    customer_orders: 0,
    merchant_accepted_orders: 0,
    recollected_orders: 0,
  });
  const [isLoadingCounts, setIsLoadingCounts] = useState(false);

  const formatDate = (isoDate: any) => {
    const date = new Date(isoDate);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
  };

  // Fetch order counts on component mount
  useEffect(() => {
    const fetchOrderCounts = async () => {
      try {
        setIsLoadingCounts(true);
        const response = await axiosInstance.get(API_URL.MERCHANT_ORDER_COUNT);
        setOrderCounts(response.data);
      } catch (error) {
        console.error("Error fetching order counts:", error);
      } finally {
        setIsLoadingCounts(false);
      }
    };

    fetchOrderCounts();
  }, []);

  // Reset dates and table data when search query changes
  useEffect(() => {
    setStartDate(null);
    setEndDate(null);
    setAllSearchResults([]);
    setHasSearched(false);
  }, [searchQuery]);

  const handleSearch = async () => {
    // Prepare payload with status as array or string based on selection
    const payload: any = {
      start_date: startDate?.toLocaleDateString("en-CA"),
      end_date: endDate?.toLocaleDateString("en-CA"),
    };

    // Always send statuses as an array
    if (searchQuery === "BOOKED") {
      payload.statuses = ["BOOKED", "ORDER_ACCEPT"];
    } else if (searchQuery === "ALL") {
      payload.statuses = [
        "BOOKED",
        "ORDER_ACCEPT",
        "MERCHANT_ORDER_ACCEPT",
        "DELIVERED",
        "RECOLLECTED",
      ];
    } else {
      payload.statuses = [searchQuery];
    }

    try {
      setIsLoading(true);
      let page = 0;
      let allData: any[] = [];
      let keepFetching = true;

      while (keepFetching) {
        const response = await axiosInstance.post(
          `${API_URL.EXPORTLIST_SEARCH_RESULTS}?size=50&page=${page}`,
          payload
        );

        const pageData = response?.data?.content || [];
        if (pageData.length === 0) {
          keepFetching = false;
        } else {
          allData = [...allData, ...pageData];
          page++;
        }
      }

      setAllSearchResults(allData);
      setHasSearched(true);
    } catch (error) {
      console.error("Error fetching search results", error);
      setHasSearched(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to check if recollection date is within 3 days from today
  const isRecollectionWithin3Days = (recollectionDate: string) => {
    if (!recollectionDate) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day

    const recollection = new Date(recollectionDate);
    recollection.setHours(0, 0, 0, 0); // Reset time to start of day

    const diffTime = recollection.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // Check if recollection date is within 0 to 3 days from today
    return diffDays >= 0 && diffDays <= 3;
  };

  // Helper function to convert hex color to Excel RGB format
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : { r: 255, g: 255, b: 255 };
  };

  // Helper function to get Excel fill color based on order
  const getExcelFillColor = (order: any) => {
    const hexColor = getRowBackgroundColor(order);
    if (hexColor === "transparent") return null;

    const rgb = hexToRgb(hexColor);
    // Convert RGB to hex string for Excel (without # prefix)
    const rgbHex = ((rgb.r << 16) | (rgb.g << 8) | rgb.b)
      .toString(16)
      .padStart(6, "0")
      .toUpperCase();
    return rgbHex;
  };

  const handleExport = () => {
    if (!allSearchResults || allSearchResults.length === 0) return;

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const wsData: any[][] = [];

    // Add header row
    wsData.push([
      "S.No",
      "Order Created Date",
      "Order Number",
      "LPO No",
      "Number of Days",
      "Customer Name",
      "Phone Number",
      "Product Name",
      "Location",
      "Extended days",
      "Plate Number",
      "Delivery Date and Time",
      "Recollection Date and Time",
      "Order Status",
    ]);

    // Add data rows
    allSearchResults.forEach((order, index) => {
      wsData.push([
        index + 1,
        formatDate(order?.createdAt) || "N/A",
        order?.orderId || "N/A",
        order?.erp_service_number || "N/A",
        order?.rental_days || "N/A",
        order?.customer_name || "N/A",
        order?.customer_phone_number || "N/A",
        order?.products?.[0]?.productName || "N/A",
        order?.delivery_location?.address || "N/A",
        order?.extended_rental_days || "N/A",
        order?.plate_number || "N/A",
        formatDate(order?.delivery_date) || "N/A",
        formatDate(order?.recollection_date) || "N/A",
        order?.orderStatus || "N/A",
      ]);
    });

    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Apply background colors to rows based on order status
    const range = XLSX.utils.decode_range(ws["!ref"] || "A1");
    const numCols = range.e.c + 1;

    allSearchResults.forEach((order, index) => {
      const rowIndex = index + 1; // +1 because row 0 is header
      const fillColor = getExcelFillColor(order);

      if (fillColor) {
        // Apply color to all cells in the row
        for (let col = 0; col < numCols; col++) {
          const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: col });
          if (!ws[cellAddress]) continue;

          ws[cellAddress].s = {
            fill: {
              patternType: "solid",
              fgColor: { rgb: fillColor },
            },
            font: {
              color: { rgb: "000000" },
            },
          };
        }
      }
    });

    XLSX.utils.book_append_sheet(wb, ws, "Orders");

    const currentDate = new Date().toISOString().split("T")[0];
    const currentTime = new Date()
      .toLocaleTimeString("en-US", { hour12: false })
      .replace(/:/g, "_");
    const filename = `ServiceList-${currentDate}_${currentTime}_${searchQuery}.xlsx`;

    XLSX.writeFile(wb, filename, { cellStyles: true, bookSST: false });
  };

  const ordersToDisplay = useMemo(() => {
    return hasSearched ? allSearchResults : [];
  }, [allSearchResults, hasSearched]);

  // Helper function to get row background color based on order
  const getRowBackgroundColor = (order: any) => {
    const orderStatus = order?.orderStatus || order;

    switch (orderStatus) {
      case "BOOKED":
      case "ORDER_ACCEPT":
        return "#AAFF00"; // NEON GREEN
      case "MERCHANT_ORDER_ACCEPT":
        return "#40E0D0"; // ELECTRIC CYAN/AQUA
      case "DELIVERED":
        // Check if recollection date is within 3 days
        if (
          order?.recollection_date &&
          isRecollectionWithin3Days(order.recollection_date)
        ) {
          return "#FFD580"; // PALE ORANGE
        }
        return "#FF8C00"; // BRIGHT RED-ORANGE
      case "RECOLLECTED":
        return "#FFFACD"; // PURE YELLOW
      default:
        return "transparent";
    }
  };

  return (
    <div className="row">
      <div className="col-md-12 mb-3 mt-2">
        {/* Order Counts Section */}
        <div className="mb-2">
          {isLoadingCounts ? (
            <div className="text-center py-1 small">Loading...</div>
          ) : (
            <div className="d-flex flex-wrap gap-1">
              <div
                className="p-1 flex-grow-1 rounded"
                style={{
                  minWidth: "80px",
                  backgroundColor: "#f6f9f1ff",
                  fontSize: "0.8rem",
                }}
              >
                <div className="text-muted small">All</div>
                <div className="fw-bold">{orderCounts.all_orders}</div>
              </div>
              <div
                className="p-1 flex-grow-1 rounded"
                style={{
                  minWidth: "80px",
                  backgroundColor: "#AAFF00",
                  fontSize: "0.8rem",
                }}
              >
                <div className="small">Customer</div>
                <div className="fw-bold">{orderCounts.customer_orders}</div>
              </div>
              <div
                className="p-1 flex-grow-1 rounded"
                style={{
                  minWidth: "80px",
                  backgroundColor: "#40E0D0",
                  fontSize: "0.8rem",
                }}
              >
                <div className="small">Accepted</div>
                <div className="fw-bold">
                  {orderCounts.merchant_accepted_orders}
                </div>
              </div>
              <div
                className="p-1 flex-grow-1 rounded"
                style={{
                  minWidth: "80px",
                  backgroundColor: "#FF8C00",
                  fontSize: "0.8rem",
                }}
              >
                <div className="small">Delivered</div>
                <div className="fw-bold">{orderCounts.delivered_orders}</div>
              </div>
              <div
                className="p-1 flex-grow-1 rounded"
                style={{
                  minWidth: "80px",
                  backgroundColor: "#FFFACD",
                  fontSize: "0.8rem",
                }}
              >
                <div className="small">Recollected</div>
                <div className="fw-bold">{orderCounts.recollected_orders}</div>
              </div>
            </div>
          )}
        </div>
        <Form.Group as={Row} className="align-items-center">
          <Col sm="3">
            <Form.Select
              className="custom-select"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            >
              <option value="">Select Status</option>
              <option value="ALL">All Orders</option>
              <option value="BOOKED">Customer Orders</option>
              <option value="MERCHANT_ORDER_ACCEPT">Accepted Orders</option>
              <option value="DELIVERED">Delivered Orders</option>
              <option value="RECOLLECTED">Recollected Orders</option>
            </Form.Select>
          </Col>
          <Col sm="5" className="d-flex gap-2 align-items-center">
            <DatePicker
              selected={startDate}
              onChange={(date: Date) => setStartDate(date)}
              selectsStart
              startDate={startDate}
              endDate={endDate}
              maxDate={endDate || new Date()}
              className="search-bar-export"
              dateFormat="dd-MM-yyyy"
              placeholderText="DD-MM-YYYY"
              customInput={
                <FormControl
                  value={
                    startDate
                      ? `${String(startDate.getDate()).padStart(
                          2,
                          "0"
                        )}-${String(startDate.getMonth() + 1).padStart(
                          2,
                          "0"
                        )}-${startDate.getFullYear()}`
                      : ""
                  }
                  readOnly
                  className="input-date"
                />
              }
            />
            <span>to</span>
            <DatePicker
              selected={endDate}
              onChange={(date: Date) => setEndDate(date)}
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate}
              maxDate={new Date()}
              className="search-bar-export"
              dateFormat="dd-MM-yyyy"
              placeholderText="DD-MM-YYYY"
              customInput={
                <FormControl
                  value={
                    endDate
                      ? `${String(endDate.getDate()).padStart(2, "0")}-${String(
                          endDate.getMonth() + 1
                        ).padStart(2, "0")}-${endDate.getFullYear()}`
                      : ""
                  }
                  readOnly
                  className="input-date"
                />
              }
            />
          </Col>
          <Col sm="2">
            <Button
              className="btn all_button_style text-black my-1"
              onClick={handleSearch}
              disabled={!searchQuery || !startDate || !endDate}
            >
              Search
            </Button>
          </Col>
          <Col sm="2">
            <Button
              className="btn all_button_style text-black my-1"
              onClick={handleExport}
              disabled={!hasSearched || isLoading}
            >
              Export
            </Button>
          </Col>
        </Form.Group>
      </div>
      <div className="col-md-12">
        <LoaderExportFileKirkosModal show={isLoading} />
      </div>
      <div className="col-12 dashboard-table-div">
        <table className="table table-hover dashboard-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Order Created Date</th>
              <th>Order Number</th>
              <th>LPO No</th>
              <th>Number of Days</th>
              <th>Customer Name</th>
              <th>Phone Number</th>
              <th>Product Name</th>
              <th>Location</th>
              <th>Extended days</th>
              <th>Plate Number</th>
              <th>Delivery Date and Time</th>
              <th>Recollection Date and Time</th>
              <th>Order Status</th>
            </tr>
          </thead>
          <tbody>
            {hasSearched && ordersToDisplay.length === 0 && (
              <tr>
                <td colSpan={14} className="text-center py-4">
                  No search results found
                </td>
              </tr>
            )}
            {ordersToDisplay.map((order: any, index: number) => (
              <tr
                key={index}
                style={{
                  backgroundColor: getRowBackgroundColor(order),
                }}
              >
                <td style={{ color: "#000000" }}>{index + 1}</td>
                <td style={{ color: "#000000" }}>
                  {formatDate(order?.createdAt)}
                </td>
                <td style={{ color: "#000000" }}>{order?.orderId}</td>
                <td style={{ color: "#000000" }}>
                  {order?.erp_service_number}
                </td>
                <td style={{ color: "#000000" }}>{order?.rental_days}</td>
                <td style={{ color: "#000000" }}>{order?.customer_name}</td>
                <td style={{ color: "#000000" }}>
                  {order?.customer_phone_number}
                </td>
                <td style={{ color: "#000000" }}>
                  {order?.products?.[0]?.productName}
                </td>
                <td style={{ color: "#000000" }}>
                  {order?.delivery_location?.address}
                </td>
                <td style={{ color: "#000000" }}>
                  {order?.extended_rental_days}
                </td>
                <td style={{ color: "#000000" }}>{order?.plate_number}</td>
                <td style={{ color: "#000000" }}>
                  {formatDate(order?.delivery_date)}
                </td>
                <td style={{ color: "#000000" }}>
                  {formatDate(order?.recollection_date)}
                </td>
                <td style={{ color: "#000000" }}>{order?.orderStatus}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ConfirmationModal
        isOpen={false}
        onClose={() => {}}
        onConfirm={() => {}}
        message=""
      />
    </div>
  );
};

export default ExportorderStatus;
